import { useEffect } from 'react';
import { ArrowUpRight, Check, ThumbsUp } from 'lucide-react';
import { useServicesVideo } from './useServicesVideo';
import './Process.css';

// The original three texts/icons remain unchanged. Step four is taken from the
// supplied screenshot, as explicitly approved by the user.
const STEPS = [
  { title: 'Sie erzählen.', text: 'Was dürfen wir für Sie tun? Schildern Sie uns Ihre Wünsche – per Formular, Telefon oder E-Mail.', icon: ArrowUpRight },
  { title: 'Wir planen.', text: 'Gemeinsam klären wir den Bedarf, bei Bedarf auch vor Ort. Sie erhalten ein individuelles, unverbindliches Angebot.', icon: ArrowUpRight },
  { title: 'Alles glänzt.', text: 'Wir vereinbaren einen Termin und kümmern uns um den Rest. Einmalig oder regelmäßig – ganz wie Sie es brauchen.', icon: Check },
  { title: 'Qualitätssicherung', text: 'Kontinuierliche Kontrolle und Optimierung für höchste Kundenzufriedenheit.', icon: ThumbsUp },
];

export const Process = () => {
  const { videoRef, source, poster, canRun, playing, hasPlayed, onPlaying, onPause, onError } = useServicesVideo({
    assetName: 'betaris-process', posterName: 'process-poster',
  });

  useEffect(() => {
    const video = videoRef.current;
    // The keyed element below is replaced when switching to a still image.
    // Dispose the previous decoder as well; no change to other video controllers.
    return () => {
      if (!video) return;
      video.pause(); video.removeAttribute('src'); video.load();
    };
  }, [videoRef, source]);

  return <section className="process-experience" id="so-arbeiten-wir" data-testid="process-section" aria-labelledby="process-heading-title">
    <div className="process-background" data-testid="process-background" aria-hidden="true">
      <video key={source || 'process-still'} ref={videoRef} id="betaris-process-video" data-testid="process-video" className="process-background-video"
        src={source || undefined} poster={poster} autoPlay={canRun} muted loop playsInline preload="none" tabIndex={-1}
        width="1280" height="720" onPlaying={onPlaying} onPause={onPause} onError={onError}
        disablePictureInPicture disableRemotePlayback controlsList="nodownload nofullscreen noremoteplayback" />
      <img src={poster} alt="" width="1280" height="720" loading="lazy" data-testid="process-video-poster"
        className={`process-background-poster${hasPlayed ? ' has-played' : ''}`} />
      <div className="process-background-wash" />
    </div>

    <div className="process-content" data-testid="process-content">
      <header className="process-heading-block">
        <span className="process-kicker" data-testid="process-section-label">DER Prozess</span>
        <h2 id="process-heading-title" data-testid="process-heading">So arbeiten wir</h2>
        <p className="process-introduction" data-testid="process-introduction">Vier einfache Schritte zu Ihrer perfekten Lösung – transparent, effizient und immer auf Ihre Bedürfnisse abgestimmt.</p>
      </header>

      <ol className="process-work-grid" data-testid="process-cards-grid">
        {STEPS.map(({ title, text, icon: Icon }, index) => <li key={title} className="process-work-card" tabIndex={0}
          data-testid={`process-card-${index}`} aria-labelledby={`process-title-${index}`} aria-describedby={`process-description-${index}`}>
          <span className="process-work-number" data-testid={`process-step-number-${index}`} aria-label={`Schritt ${index + 1}`}>0{index + 1}</span>
          <span className="process-work-icon" data-testid={`process-step-icon-${index}`} aria-hidden="true"><Icon size={30} strokeWidth={1.55} /></span>
          <h3 id={`process-title-${index}`} data-testid={`process-step-title-${index}`}>{title}</h3>
          <p id={`process-description-${index}`} data-testid={`process-step-text-${index}`}>{text}</p>
        </li>)}
      </ol>

      <div className="process-contact-line"><a href="#kontakt" className="process-contact-link" data-testid="process-inquiry-link">Machen wir den ersten Schritt<ArrowUpRight size={17} /></a></div>
      <span className="sr-only" role="status" data-testid="process-video-status">{playing ? 'Der Prozesshintergrund läuft stumm in Endlosschleife.' : 'Unbewegte Vorschau des Prozesshintergrunds.'}</span>
    </div>
  </section>;
};